import * as React from "react";
import Icon from "../../Icon";
import { motion } from "framer-motion";
import AddToCalendar from "@culturehq/add-to-calendar";
import "@culturehq/add-to-calendar/dist/styles.css";
import { IParts } from "@src/components/Parts";
import DialogModal from "../../Dialog";
import LiveSchedules from "../../LiveSchedules";
import { GoogleMap, useLoadScript, MarkerF } from "@react-google-maps/api";
import Button from "@src/components/Button";
import formatDateWithOrdinal, {
  formatEventTimeWithTimeZoneLabel,
} from "@src/utils/dateFormatter";
import { useEventParts } from "@src/hooks/useEventParts";

const BG_COLOR = "bg-[#4A2C47]";
const CARD_BG = "bg-[#FEFEFE]";
const CARD_BORDER = "border-[#4A2C47]/20";
const BUTTON_BG = "bg-[#FEFEFE]";
const BUTTON_TEXT = "text-[#4A2C47]";
const TITLE_TEXT = "text-[#FEFEFE]";
const TEXT_PRIMARY = "text-[#4A2C47]";
const TEXT_ACCENT = "text-[#FEFEFE]";

const Parts: React.FC<IParts> = ({
  eventId,
  parts,
  eventPartsData,
  liveSchedules,
  eventTimezone,
  isInvitedToEvent,
}) => {
  const [openMap, setOpenMap] = React.useState<boolean>(false);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "",
    libraries: ["places"],
  });

  const onLoadMarker = (marker: any) => {
    // console.log("Marker", marker?.position?.lat);
  };

  const handleViewDirections = ({
    latitude,
    longitude,
  }: {
    latitude: number;
    longitude: number;
  }) => {
    const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}&travelmode=driving`;
    window.open(directionsUrl, "_blank");
  };

  const handleFindHotelsNearby = ({
    latitude,
    longitude,
  }: {
    latitude: number;
    longitude: number;
  }) => {
    const hotelsUrl = `https://www.google.com/maps/search/hotels/@${latitude},${longitude},15z`;
    window.open(hotelsUrl, "_blank");
  };

  const { parts: derivedParts } = useEventParts({
    eventPartsData,
    liveSchedules,
    eventTimezone,
    isInvitedToEvent,
  });
  const visibleParts = parts ?? derivedParts;

  const addOneDay = (dateTime: string) => {
    var date = new Date(dateTime);
    date.setHours(date.getHours() + 24);
    var isoDate = date.toISOString();
    return isoDate;
  };

  return visibleParts.length > 0 ? (
    <div className={`py-8 relative ${BG_COLOR} border-y border-[#FEFEFE]`}>
      <div className="absolute inset-0" />

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={{
          hidden: { opacity: 0 },
          visible: {
            opacity: 1,
            transition: { delayChildren: 0.3, staggerChildren: 0.15 },
          },
        }}
        className="relative z-10 py-4 lg:py-8 px-4"
      >
        <motion.div
          variants={{
            hidden: { opacity: 0, y: -30 },
            visible: {
              opacity: 1,
              y: 0,
              transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] },
            },
          }}
          className="text-center mb-8"
        >
          <h3
            className={`font-dacing font-semibold lg:font-normal text-4xl lg:text-6xl ${TITLE_TEXT} mb-4 tracking-wide`}
          >
            The Event Schedule
          </h3>
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: "6rem" }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className={`h-1 ${TITLE_TEXT} mx-auto rounded-full bg-[#FEFEFE]`}
          ></motion.div>
        </motion.div>

        {/* Event Cards */}
        <motion.div
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: { delayChildren: 0.3, staggerChildren: 0.15 },
            },
          }}
          className="max-w-5xl mx-auto space-y-6"
        >
          {visibleParts.map((item, idx) => (
            <motion.div
              key={`event-${idx}`}
              variants={{
                hidden: { opacity: 0, y: 50, scale: 0.95 },
                visible: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] },
                },
              }}
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              <div
                className={`relative p-4 lg:p-8 ${CARD_BG} backdrop-blur-xl rounded-3xl border ${CARD_BORDER} shadow-xl lg:hover:shadow-2xl transition-all duration-500 w-full lg:hover:scale-[1.03]`}
              >
                <div className="flex flex-col lg:flex-row items-center justify-between gap-4 lg:gap-8">
                  {/* Event Icon/Number */}
                  <div className="flex-shrink-0">
                    <div
                      className={`w-12 h-12 lg:w-20 lg:h-20 ${BUTTON_BG} rounded-full border border-[#4A2C47] flex items-center justify-center ${BUTTON_TEXT} font-bold text-lg lg:text-2xl shadow-lg`}
                    >
                      {idx + 1}
                    </div>
                  </div>
                  {/* Event Details */}
                  <div className="flex-1 text-center lg:text-left space-y-3">
                    <h4 className="text-2xl lg:text-3xl font-serif text-[#4A2C47] tracking-wide whitespace-normal break-words">
                      {item.name}
                    </h4>
                    <div className="flex flex-col lg:flex-row gap-4 lg:gap-8 text-[#4A2C47]">
                      {/* Date */}
                      <div className="flex items-center justify-center lg:justify-start gap-2">
                        <span className="font-medium">
                          {/* Add Icon Here!!! */}
                          {formatDateWithOrdinal(
                            item.time,
                            item.eventPartTimezone ?? eventTimezone
                          )}
                        </span>
                      </div>
                      {/* Time */}
                      <div className="flex items-center justify-center lg:justify-start gap-2">
                        <span className="font-medium">
                          {/* Add Icon Here!!! */}
                          {formatEventTimeWithTimeZoneLabel(
                            item.time,
                            item.eventPartTimezone ?? eventTimezone
                          )}
                        </span>
                      </div>
                      {/* Address */}
                      {item.showAddress && (
                        <div className="flex items-center justify-center lg:justify-start gap-2">
                          <span className="font-medium max-w-xs text-[#4A2C47]">
                            {/* ICONNNNNNNNNNN */}
                            {item?.address}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Action Buttons */}
                  <div className="flex flex-col gap-3 items-stretch">
                    {item.showAddress && (
                      <div className="inline-flex items-center justify-center w-[140px] bg-[#FEFEFE] text-[#4A2C47] border border-[#4A2C47] rounded-full font-medium hover:bg-[#4A2C47] transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer group">
                        <AddToCalendar
                          event={{
                            name: item.name,
                            details: null,
                            location: item?.address,
                            startsAt: item?.time,
                            endsAt: addOneDay(item?.time),
                          }}
                        >
                          <span
                            className={`flex items-center justify-center text-[#4A2C47] font-medium group-hover:text-[#FEFEFE] transition-all duration-300`}
                          >
                            Save Date
                          </span>
                        </AddToCalendar>
                      </div>
                    )}
                    {item.showAddress &&
                      item.hasLiveStream &&
                      liveSchedules && (
                        <DialogModal
                          fillScreen={true}
                          trigger={
                            <button
                              className={`px-6 py-3 w-[140px] bg-[#FEFEFE] text-[#4A2C47] border border-[#4A2C47] rounded-full font-medium hover:bg-[#4A2C47] hover:text-[#FEFEFE] transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer`}
                            >
                              Watch Live
                            </button>
                          }
                        >
                          <LiveSchedules schedules={liveSchedules} />
                        </DialogModal>
                      )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom decorative section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="text-center mt-16 px-4"
        ></motion.div>
      </motion.div>
    </div>
  ) : null;
};

export default Parts;
